/**
 * Premium Collection Grid AJAX & Switcher Controller
 */

class CollectionGridManager {
  constructor() {
    this.wrapper = document.querySelector('.collection-page-wrapper');
    if (!this.wrapper) return;

    this.grid = document.getElementById('CollectionProductGrid');
    this.drawer = document.getElementById('FilterDrawer');
    this.form = document.getElementById('FacetFiltersForm');
    this.sortSelect = document.querySelector('[data-sort-select]');
    this.switcher = document.querySelector('[data-grid-switcher]');

    this.init();
  }

  init() {
    this.initGridSwitcher();
    this.bindEvents();
  }

  bindEvents() {
    // Open/Close filter drawer
    const filterTrigger = document.querySelector('[data-filter-trigger]');
    if (filterTrigger && this.drawer) {
      filterTrigger.addEventListener('click', () => this.openFilter());
    }

    if (this.drawer) {
      const closeBtn = this.drawer.querySelector('[data-filter-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.closeFilter());
      }
      const overlay = this.drawer.querySelector('[data-filter-overlay]');
      if (overlay) {
        overlay.addEventListener('click', () => this.closeFilter());
      }
    }

    // Sort By selector trigger
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', (e) => {
        this.onFilterChange();
      });
    }

    // Form inputs change (checkboxes/ranges)
    if (this.form) {
      this.form.addEventListener('change', () => this.onFilterChange());
      
      // Prevent standard form submission redirects
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.onFilterChange();
      });

      // Bind Apply button inside filter drawer
      const applyBtn = this.drawer.querySelector('[data-filter-apply-btn]');
      if (applyBtn) {
        applyBtn.addEventListener('click', () => {
          this.onFilterChange();
          this.closeFilter();
        });
      }
    }

    // Intercept pagination clicks and grid interactions (using event delegation)
    document.addEventListener('click', (e) => {
      // Pagination link click
      const pagLink = e.target.closest('[data-pagination-link]');
      if (pagLink) {
        e.preventDefault();
        const url = pagLink.getAttribute('href');
        this.updateGrid(url, true); // scroll to top
      }
    });
  }

  openFilter() {
    if (this.drawer) {
      this.drawer.classList.add('is-active');
      document.body.style.overflow = 'hidden';
    }
  }

  closeFilter() {
    if (this.drawer) {
      this.drawer.classList.remove('is-active');
      document.body.style.overflow = '';
    }
  }

  initGridSwitcher() {
    if (!this.switcher || !this.grid) return;

    const switchBtns = this.switcher.querySelectorAll('.grid-switch-btn');
    
    // Read cached layout preference from localStorage
    const savedCols = localStorage.getItem('collection-grid-columns') || this.grid.getAttribute('data-desktop-columns') || '4';
    this.switchColumns(savedCols);

    switchBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const cols = btn.getAttribute('data-columns');
        this.switchColumns(cols);
      });
    });
  }

  switchColumns(columnsCount) {
    if (!this.grid || !this.switcher) return;

    // Update active class on selector buttons
    const switchBtns = this.switcher.querySelectorAll('.grid-switch-btn');
    switchBtns.forEach(btn => {
      if (btn.getAttribute('data-columns') === columnsCount) {
        btn.classList.add('grid-switch-btn--active');
      } else {
        btn.classList.remove('grid-switch-btn--active');
      }
    });

    // Remove old layout cols and apply the chosen grid columns
    this.grid.className = this.grid.className.replace(/\bgrid-cols-\d+/g, '');
    this.grid.classList.add(`grid-cols-${columnsCount}`);

    // Persist in storage
    localStorage.setItem('collection-grid-columns', columnsCount);
  }

  onFilterChange() {
    let params = [];

    // Serialize form parameters
    if (this.form) {
      const formData = new FormData(this.form);
      for (const [key, value] of formData.entries()) {
        if (value) {
          params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      }
    }

    // Add Sort By value
    if (this.sortSelect) {
      params.push(`sort_by=${encodeURIComponent(this.sortSelect.value)}`);
    }

    const queryString = params.join('&');
    const targetUrl = `${window.location.pathname}?${queryString}`;

    this.updateGrid(targetUrl);
  }

  updateGrid(url, scrollToTop = false) {
    // Show a subtle loading indicators
    if (this.grid) this.grid.style.opacity = '0.5';

    // Parse target sections
    const renderUrl = `${url}${url.includes('?') ? '&' : '?'}sections=main-collection-product-grid`;

    fetch(renderUrl)
    .then(response => response.json())
    .then(data => {
      const renderedHtml = data['main-collection-product-grid'];
      if (renderedHtml) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = renderedHtml;

        // 1. Replace Grid listings
        const newGrid = tempDiv.querySelector('#CollectionProductGrid');
        if (newGrid && this.grid) {
          this.grid.innerHTML = newGrid.innerHTML;
          this.grid.style.opacity = '1';
        }

        // 2. Replace Pagination elements
        const currentPaginator = this.wrapper.querySelector('.collection-grid__pagination');
        const newPaginator = tempDiv.querySelector('.collection-grid__pagination');
        if (currentPaginator && newPaginator) {
          currentPaginator.innerHTML = newPaginator.innerHTML;
        } else if (currentPaginator && !newPaginator) {
          currentPaginator.innerHTML = ''; // Hide pagination
        } else if (!currentPaginator && newPaginator) {
          // Re-insert paginator if it didn't exist before
          const gridContainer = this.wrapper.querySelector('.collection-grid-container');
          if (gridContainer) {
            const paginationDiv = document.createElement('div');
            paginationDiv.className = 'collection-grid__pagination';
            paginationDiv.innerHTML = newPaginator.innerHTML;
            gridContainer.appendChild(paginationDiv);
          }
        }

        // 3. Replace Count labels
        const currentCount = this.wrapper.querySelector('.collection-grid__products-count');
        const newCount = tempDiv.querySelector('.collection-grid__products-count');
        if (currentCount && newCount) {
          currentCount.textContent = newCount.textContent;
        }

        // 4. Replace filters sidebar elements (sync checked status asynchronously)
        const newDrawerForm = tempDiv.querySelector('#FacetFiltersForm');
        if (newDrawerForm && this.form) {
          this.form.innerHTML = newDrawerForm.innerHTML;
        }

        // Reapply column swatches preferences to the freshly rendered grid
        const savedCols = localStorage.getItem('collection-grid-columns') || '4';
        this.switchColumns(savedCols);

        // Update active Browser history endpoint URL
        history.pushState(null, '', url);

        // Scroll to grid container top smoothly if triggered
        if (scrollToTop) {
          const bar = this.wrapper.querySelector('.collection-controls-bar');
          if (bar) {
            bar.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }
    })
    .catch(error => {
      console.error('Error fetching collection results:', error);
      if (this.grid) this.grid.style.opacity = '1';
    });
  }
}

// Instantiate manager on load
document.addEventListener('DOMContentLoaded', () => {
  window.collectionGrid = new CollectionGridManager();
});
